import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Star, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { OUTCOME_TYPES, xhrJson } from './outcomeApi';

const emptyForm = (partner) => ({
  outcome_type: partner?.post_meeting_due ? 'meeting_result' : 'will_refer',
  note: '', meeting_at: '', followup_at: '', potential_referrals: '',
  star_rating: partner?.star_rating || 0,
});

function RatingPicker({ value, onChange }) {
  return (
    <div className="p-3 rounded-md border-2 border-orange-200 bg-orange-50" data-testid="outcome-rating-row">
      <Label className="text-sm font-semibold">Influence rating (1-5) *</Label>
      <p className="text-xs text-muted-foreground mb-2">Required when a partner says they will refer — helps us weigh commitments.</p>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button" onClick={() => onChange(value === n ? 0 : n)}
            data-testid={`outcome-rating-star-${n}`} className="p-1 hover:scale-110 transition">
            <Star className={`w-6 h-6 ${value >= n ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300'}`} />
          </button>
        ))}
        {value > 0 && <span className="text-sm text-muted-foreground ml-2">{value}/5</span>}
      </div>
    </div>
  );
}

function OutcomeGrid({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2 mt-2" data-testid="outcome-type-grid">
      {OUTCOME_TYPES.map(o => {
        const Icon = o.icon;
        const active = value === o.key;
        return (
          <button key={o.key} type="button" onClick={() => onChange(o.key)} data-testid={`outcome-type-${o.key}`}
            className={`p-2 border rounded-md flex items-center gap-2 text-sm text-left transition ${active ? 'bg-orange-500 text-white border-orange-500' : 'hover:border-orange-300'}`}>
            <Icon className="w-4 h-4" /> {o.label}
          </button>
        );
      })}
    </div>
  );
}

const validate = (otype, form, name) => {
  if (otype === 'will_refer') {
    const n = parseInt(form.potential_referrals, 10);
    if (!n || n < 1) return 'Enter the potential number of referrals (1 or more)';
    if (!form.star_rating || form.star_rating < 1) return 'Please set a star rating (1-5) before saving "Will refer"';
  }
  if (otype === 'meeting_result') {
    const n = parseInt(form.potential_referrals, 10);
    if (Number.isNaN(n) || n < 0) return 'Enter the number of referrals received (0 if none)';
  }
  if (otype === 'meeting_set_up' && !form.meeting_at) return 'Pick the meeting date & time';
  if (otype === 'call_back_later' && !form.followup_at) return 'Pick the call-back date';
  if (otype === 'wont_refer_dormant' && !window.confirm(`Mark ${name} as dormant? They'll be excluded from the Re-engagement queue going forward.`)) return 'cancel';
  if (otype === 'not_interested' && !window.confirm(`Mark ${name} as NOT INTERESTED? They'll be hidden from the queue permanently (visible only in the Not interested view).`)) return 'cancel';
  return null;
};

export function LogOutcomeDialog({ open, onOpenChange, partner, onSaved }) {
  const [form, setForm] = useState(emptyForm(partner));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(emptyForm(partner));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partner?.user_id]);

  const active = OUTCOME_TYPES.find(o => o.key === form.outcome_type);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = () => {
    if (!partner) return;
    const otype = form.outcome_type;
    const err = validate(otype, form, partner.name || partner.email);
    if (err === 'cancel') return;
    if (err) return toast.error(err);
    const withCount = otype === 'will_refer' || otype === 'meeting_result';
    const payload = {
      partner_id: partner.user_id,
      outcome_type: otype,
      note: form.note,
      meeting_at: form.meeting_at || null,
      followup_at: form.followup_at || null,
      potential_referrals: withCount ? parseInt(form.potential_referrals, 10) : null,
      star_rating: otype === 'will_refer' ? form.star_rating : null,
    };
    setSaving(true);
    xhrJson('POST', '/admin/reengagement/outcomes', payload, (status, data) => {
      setSaving(false);
      if (status === 200) {
        toast.success('Outcome logged');
        onOpenChange(false);
        onSaved?.(data?.outcome, payload);
      } else if (status === 422) toast.error(data?.detail || 'Invalid input');
      else if (status === 0) toast.error('Network error');
      else toast.error('Failed to log outcome');
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="log-outcome-dialog">
        <DialogHeader>
          <DialogTitle>Log outcome — {partner?.name || partner?.email}</DialogTitle>
          <DialogDescription>
            {active?.cooldown && <>After this outcome: <strong>{active.cooldown}</strong>.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">Outcome</Label>
            <OutcomeGrid value={form.outcome_type} onChange={(k) => set({ outcome_type: k })} />
          </div>
          {active?.askPotential && (
            <div>
              <Label className="text-sm">Potential number of referrals *</Label>
              <Input type="number" min={1} value={form.potential_referrals}
                onChange={e => set({ potential_referrals: e.target.value })}
                placeholder="e.g. 5" data-testid="outcome-potential-referrals" />
            </div>
          )}
          {active?.askReferralsReceived && (
            <div>
              <Label className="text-sm">Referrals received after the meeting *</Label>
              <Input type="number" min={0} value={form.potential_referrals}
                onChange={e => set({ potential_referrals: e.target.value })}
                placeholder="0 if none yet" data-testid="outcome-referrals-received" />
              <p className="text-xs text-muted-foreground mt-1">Enter how many referrals this partner has actually sent so far post the meeting.</p>
            </div>
          )}
          {active?.requireRating && <RatingPicker value={form.star_rating} onChange={(v) => set({ star_rating: v })} />}
          {active?.markDormant && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" data-testid="dormant-warning">
              <strong>Heads up:</strong> This partner will be marked <strong>dormant</strong> and hidden from future Re-engagement queues.
            </div>
          )}
          {active?.key === 'not_interested' && (
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700 flex gap-2" data-testid="not-interested-warning">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>This hides the partner <strong>permanently</strong> from the queue. Visible only in the "Not interested" view. You can re-queue them from there if they change their mind.</span>
            </div>
          )}
          {active?.askMeeting && (
            <div>
              <Label className="text-sm">Meeting date & time *</Label>
              <Input type="datetime-local" value={form.meeting_at}
                onChange={e => set({ meeting_at: e.target.value })} data-testid="outcome-meeting-at" />
              <p className="text-xs text-muted-foreground mt-1">Partner will auto-resurface in the queue after this date, flagged "Post-meeting due" so you can log the result.</p>
            </div>
          )}
          {active?.askFollowup && (
            <div>
              <Label className="text-sm">Call back on *</Label>
              <Input type="date" value={form.followup_at}
                onChange={e => set({ followup_at: e.target.value })} data-testid="outcome-followup-at" />
            </div>
          )}
          <div>
            <Label className="text-sm">Notes</Label>
            <Textarea rows={3} value={form.note} onChange={e => set({ note: e.target.value })}
              placeholder="What did they say? Any context for the next call..." data-testid="outcome-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="outcome-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="outcome-submit" className="bg-orange-500 hover:bg-orange-600 text-white">
            {saving ? 'Saving...' : 'Save outcome'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
